import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import moment from "moment";
import {
  ChevronLeftRounded,
  ChevronRightRounded,
  CalendarTodayRounded,
  EventBusyRounded,
} from "@mui/icons-material";

const TimeOffCalendar = ({ timeOffRequests = [], onDateClick, selectedDate }) => {
  const [currentDate, setCurrentDate] = useState(moment());
  const [viewMode, setViewMode] = useState("month"); // month, year

  // Get time off requests for a specific date
  const getTimeOffForDate = (date, requests) => {
    return requests.filter((request) => {
      const startDate = moment(request.startDate);
      const endDate = moment(request.endDate);
      return date.isSameOrAfter(startDate, "day") && date.isSameOrBefore(endDate, "day");
    });
  };

  // Generate month view
  const generateMonthView = (date, requests) => {
    const startOfMonth = date.clone().startOf("month");
    const endOfMonth = date.clone().endOf("month");
    const startOfCalendar = startOfMonth.clone().startOf("week");
    const endOfCalendar = endOfMonth.clone().endOf("week");

    const days = [];
    const current = startOfCalendar.clone();

    while (current.isSameOrBefore(endOfCalendar)) {
      const dayData = {
        date: current.clone(),
        isCurrentMonth: current.isSame(date, "month"),
        isToday: current.isSame(moment(), "day"),
        isSelected: selectedDate && current.isSame(selectedDate, "day"),
        timeOffRequests: getTimeOffForDate(current, requests),
      };
      days.push(dayData);
      current.add(1, "day");
    }

    return {
      days,
      month: date.format("MMMM YYYY"),
      weeks: Math.ceil(days.length / 7),
    };
  };

  // Generate year view (12 months grid)
  const generateYearView = (date, requests) => {
    const months = [];
    for (let i = 0; i < 12; i++) {
      const monthDate = date.clone().month(i);
      const monthData = generateMonthView(monthDate, requests);
      months.push({
        ...monthData,
        monthName: monthDate.format("MMM"),
        monthNumber: i,
      });
    }
    return { months, year: date.format("YYYY") };
  };

  // Generate calendar data
  const calendarData = useMemo(() => {
    if (viewMode === "month") {
      return generateMonthView(currentDate, timeOffRequests);
    } else {
      return generateYearView(currentDate, timeOffRequests);
    }
  }, [currentDate, timeOffRequests, viewMode]);

  // Get status color for time off request
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "PENDING":
        return "bg-yellow-100 border-yellow-300 text-yellow-800";
      case "APPROVED":
        return "bg-green-100 border-green-300 text-green-800";
      case "REJECTED":
        return "bg-red-100 border-red-300 text-red-800";
      case "CANCELLED":
        return "bg-gray-100 border-gray-300 text-gray-800";
      default:
        return "bg-blue-100 border-blue-300 text-blue-800";
    }
  };

  // Navigation handlers
  const goToPrevious = () => {
    if (viewMode === "month") {
      setCurrentDate(currentDate.clone().subtract(1, "month"));
    } else {
      setCurrentDate(currentDate.clone().subtract(1, "year"));
    }
  };

  const goToNext = () => {
    if (viewMode === "month") {
      setCurrentDate(currentDate.clone().add(1, "month"));
    } else {
      setCurrentDate(currentDate.clone().add(1, "year"));
    }
  };

  const goToToday = () => {
    setCurrentDate(moment());
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === "month" ? "year" : "month");
  };

  // Day names for header
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 overflow-visible">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <CalendarTodayRounded className="h-5 w-5 text-indigo-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              {viewMode === "month" ? calendarData.month : calendarData.year}
            </h3>
          </div>
          <button
            onClick={toggleViewMode}
            className="px-3 py-1 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
          >
            {viewMode === "month" ? "Year View" : "Month View"}
          </button>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={goToPrevious}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeftRounded className="h-5 w-5" />
          </button>
          <button
            onClick={goToToday}
            className="px-3 py-1 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Today
          </button>
          <button
            onClick={goToNext}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRightRounded className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Calendar Body */}
      <div className="overflow-visible">
        <AnimatePresence mode="wait">
        {viewMode === "month" ? (
          <motion.div
            key={calendarData.month}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-px mb-2">
              {dayNames.map((day) => (
                <div
                  key={day}
                  className="p-3 text-center text-sm font-medium text-gray-500 bg-gray-50"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
              {calendarData.days.map((day, index) => (
                <motion.div
                  key={day.date.format("YYYY-MM-DD")}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.01 }}
                  className={`min-h-[100px] bg-white p-2 cursor-pointer hover:bg-gray-50 transition-colors ${
                    !day.isCurrentMonth ? "bg-gray-50" : ""
                  } ${day.isToday ? "ring-2 ring-indigo-500 ring-inset" : ""} ${
                    day.isSelected ? "bg-indigo-50" : ""
                  }`}
                  onClick={() => onDateClick && onDateClick(day.date)}
                >
                  {/* Date Number */}
                  <div
                    className={`text-sm font-medium mb-1 ${
                      !day.isCurrentMonth
                        ? "text-gray-400"
                        : day.isToday
                        ? "text-indigo-600"
                        : "text-gray-900"
                    }`}
                  >
                    {day.date.format("D")}
                  </div>

                  {/* Time Off Markings */}
                  <div className="space-y-1">
                    {day.timeOffRequests.slice(0, 2).map((request, idx) => (
                      <div
                        key={`${request.id}-${idx}`}
                        className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(
                          request.status
                        )} truncate`}
                        title={`${request.type} - ${request.status}`}
                      >
                        <EventBusyRounded className="inline h-3 w-3 mr-1" />
                        {request.type.replace("_", " ")}
                      </div>
                    ))}
                    {day.timeOffRequests.length > 2 && (
                      <div className="text-xs text-gray-500 px-2 py-1">
                        +{day.timeOffRequests.length - 2} more
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key={calendarData.year}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="grid grid-cols-4 gap-3 max-h-none overflow-visible"
          >
            {calendarData.months.map((month) => (
              <div key={month.monthNumber} className="space-y-1">
                <h4 className="text-xs font-semibold text-gray-700 text-center">
                  {month.monthName}
                </h4>
                {/* Mini month grid */}
                <div className="grid grid-cols-7 gap-px text-xs">
                  {dayNames.map((day) => (
                    <div
                      key={day}
                      className="p-0 text-center text-gray-400 bg-gray-50 text-[10px]"
                    >
                      {day.charAt(0)}
                    </div>
                  ))}
                  {month.days.map((day) => (
                    <div
                      key={day.date.format("YYYY-MM-DD")}
                      className={`p-0.5 text-center cursor-pointer hover:bg-gray-100 text-[10px] ${
                        !day.isCurrentMonth ? "text-gray-300" : "text-gray-700"
                      } ${day.isToday ? "bg-indigo-100 text-indigo-600" : ""} ${
                        day.timeOffRequests.length > 0
                          ? "bg-yellow-100 text-yellow-800"
                          : ""
                      }`}
                      onClick={() => {
                        setCurrentDate(day.date);
                        setViewMode("month");
                      }}
                    >
                      {day.date.format("D")}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      </div>

      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-center space-x-6 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-yellow-100 border border-yellow-300 rounded-full"></div>
            <span className="text-gray-600">Pending</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-100 border border-green-300 rounded-full"></div>
            <span className="text-gray-600">Approved</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-100 border border-red-300 rounded-full"></div>
            <span className="text-gray-600">Rejected</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-gray-100 border border-gray-300 rounded-full"></div>
            <span className="text-gray-600">Cancelled</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeOffCalendar;
